import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';

export interface ProjectTransition {
  id?: string;
  from: string | null;
  to: string;
  timestamp: string;
  reason: string;
  userId: string;
  metadata?: any;
}

export interface ProjectTimeline {
  projectId: string;
  transitions: ProjectTransition[];
  lastUpdated: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectTimelineService {

  constructor(private afs: AngularFirestore) {
  }

  /**
   * Registra una transición de estado para un proyecto
   */
  async recordTransition(
    projectId: string,
    from: string | null,
    to: string,
    reason: string,
    userId: string = 'auto-system',
    metadata?: any
  ): Promise<void> {
    
    if (!projectId) {
      console.error('❌ [DEBUG] projectId es null o undefined');
      throw new Error('projectId es requerido');
    }
    
    const timelineRef = this.afs.collection('projects-timeline').doc(projectId);
    
    try {
      // Crear la nueva transición
      const newTransition: ProjectTransition = {
        id: this.generateTransitionId(),
        from: from,
        to: to,
        timestamp: new Date().toISOString(),
        reason: reason,
        userId: userId,
        ...(metadata && { metadata: metadata }) // Solo incluir metadata si no es undefined
      };

      // Obtener el timeline existente o crear uno nuevo
      const timelineDoc = await timelineRef.get().toPromise();
      
      if (timelineDoc?.exists) {
        // Actualizar timeline existente
        const timelineData = timelineDoc.data() as ProjectTimeline;
        timelineData.transitions.push(newTransition);
        timelineData.lastUpdated = new Date().toISOString();
        
        // Limpiar el objeto para eliminar campos undefined
        const cleanTimelineData = JSON.parse(JSON.stringify(timelineData));
        await timelineRef.update(cleanTimelineData);
      } else {
        // Crear nuevo timeline
        const newTimeline: ProjectTimeline = {
          projectId: projectId,
          transitions: [newTransition],
          lastUpdated: new Date().toISOString()
        };
        
        // Limpiar el objeto para eliminar campos undefined
        const cleanTimeline = JSON.parse(JSON.stringify(newTimeline));
        
        await timelineRef.set(cleanTimeline);
      }
      
    } catch (error: any) {
      console.error(`❌ [DEBUG] Error registrando transición:`, error);
      console.error(`❌ [DEBUG] Error details:`, {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      throw error;
    }
  }

  /**
   * Obtiene todas las transiciones de un proyecto
   */
  async getProjectTimeline(projectId: string): Promise<ProjectTransition[]> {
    try {
      const timelineDoc = await this.afs.collection('projects-timeline').doc(projectId).get().toPromise();
      
      if (timelineDoc?.exists) {
        const timelineData = timelineDoc.data() as ProjectTimeline;
        return timelineData.transitions.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      }
      
      return [];
    } catch (error) {
      console.error(`❌ Error obteniendo timeline: ${error}`);
      return [];
    }
  }

  /**
   * Obtiene la fecha de una transición específica
   */
  async getTransitionDate(projectId: string, fromStatus: string, toStatus: string): Promise<Date | null> {
    const transitions = await this.getProjectTimeline(projectId);
    
    const transition = transitions.find(t => t.from === fromStatus && t.to === toStatus);
    return transition ? new Date(transition.timestamp) : null;
  }

  /**
   * Obtiene la fecha de financiación
   */
  async getFundedDate(projectId: string): Promise<Date | null> {
    return this.getTransitionDate(projectId, 'Activo', 'Financiado');
  }

  /**
   * Obtiene la fecha de inicio de "En curso"
   */
  async getInProgressDate(projectId: string): Promise<Date | null> {
    return this.getTransitionDate(projectId, 'Financiado', 'En curso');
  }

  /**
   * Obtiene el tiempo transcurrido en el estado actual
   */
  async getTimeInCurrentStatus(projectId: string): Promise<number> {
    const transitions = await this.getProjectTimeline(projectId);
    
    if (transitions.length > 0) {
      const lastTransition = transitions[transitions.length - 1];
      const transitionDate = new Date(lastTransition.timestamp);
      const now = new Date();
      return now.getTime() - transitionDate.getTime();
    }
    
    return 0;
  }

  /**
   * Genera un ID único para la transición
   */
  private generateTransitionId(): string {
    return Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
  }
} 